import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { QaBadge } from "@/components/QaBadge";
import { PayBadge } from "@/components/PayBadge";
import { AuditTrail } from "@/components/AuditTrail";
import { EvidenceChain } from "@/components/EvidenceChain";
import { DocumentVault } from "@/components/DocumentVault";
import { DocumentRequests } from "@/components/DocumentRequests";
import { SkipTracePanel } from "@/components/SkipTracePanel";
import { ClientDecisionPanel } from "@/components/ClientDecisionPanel";
import { AddAddressOptions } from "@/components/client/AddAddressOptions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Download, FileText, ArrowLeft, UserPlus, CheckCircle2, XCircle, MapPin, ChevronsUpDown, Check } from "lucide-react";
import { NeedHelp } from "@/components/NeedHelp";
import { PayInvoiceButton } from "@/components/PayInvoiceButton";
import { PrintingCharge } from "@/components/PrintingCharge";

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value || "—"}</span>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user.role === "admin";
  const [order, setOrder] = useState(null);
  const [refundBusy, setRefundBusy] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [servers, setServers] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [printBillable, setPrintBillable] = useState("server");
  const [printBlocked, setPrintBlocked] = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignPickerOpen, setReassignPickerOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignNote, setReassignNote] = useState("");
  const [repl, setRepl] = useState(null);
  const [windowsOpen, setWindowsOpen] = useState(false);
  const [editWindows, setEditWindows] = useState([]);
  const [windowsReason, setWindowsReason] = useState("");

  const openWindows = () => {
    setEditWindows((order.service_windows || []).map((w) => ({ ...w })));
    setWindowsReason("");
    setWindowsOpen(true);
  };
  const saveWindows = async () => {
    if (!editWindows.length) return toast.error("At least one window is required");
    setBusy(true);
    try {
      await api.put(`/orders/${id}/service-windows`, {
        windows: editWindows.map((w) => ({ key: w.key, label: w.label, display: w.display, start: w.start, end: w.end, status: w.status })),
        reason: windowsReason,
      });
      toast.success("Service windows updated");
      setWindowsOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update windows");
    } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  const loadReplacements = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}/eligible-replacements`);
      setRepl(data);
    } catch { setRepl(null); }
  }, [id]);

  useEffect(() => {
    load();
    if (isAdmin) api.get("/servers").then(({ data }) => setServers(data)).catch(() => {});
  }, [load, isAdmin]);

  useEffect(() => {
    if (isAdmin && order && order.assigned_server_id) loadReplacements();
  }, [isAdmin, order, loadReplacements]);

  if (notFound)
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <p className="font-display text-xl font-bold text-slate-900">Order not found</p>
        <p className="mt-1 text-slate-500">This job doesn't exist or you don't have access.</p>
        <Button data-testid="notfound-back-button" onClick={() => nav(isAdmin ? "/admin/jobs" : "/client")} className="mt-5 bg-slate-900 text-white hover:bg-slate-800">Back</Button>
      </div>
    );
  if (!order) return <p className="text-slate-400">Loading…</p>;

  const addr = order.service_address || {};
  const backTo = isAdmin ? "/admin/jobs" : "/client";

  const assign = async () => {
    if (!assignTo) return toast.error("Select a server");
    setBusy(true);
    try {
      await api.post(`/orders/${id}/assign`, { server_id: assignTo });
      toast.success("Server assigned");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const review = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/review`, { decision, notes: reviewNotes, print_billable_to: printBillable });
      toast.success(decision === "approve" ? "Proof approved & sealed" : "Proof returned for revision");
      setReviewNotes("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reassign = async () => {
    if (!reassignTo) return toast.error("Select a replacement server");
    if (!reassignReason.trim()) return toast.error("A reassignment reason is required");
    setBusy(true);
    try {
      await api.post(`/orders/${id}/reassign`, { server_id: reassignTo, reason: reassignReason, note: reassignNote });
      toast.success("Job reassigned to new server");
      setReassignOpen(false); setReassignTo(""); setReassignReason(""); setReassignNote("");
      await load();
      await loadReplacements();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await loadReplacements();
    }
    finally { setBusy(false); }
  };

  // Affidavit view/download must use the SAME authenticated API mechanism as the Document
  // Vault (axios cookie session + silent refresh, or a short-lived file-scoped view token).
  // A raw window.open/<a href> top-level navigation cannot run the silent refresh, so once
  // the 15-min access-token cookie lapses the backend returns "Not authenticated".
  const downloadAffidavit = async () => {
    const fid = order.affidavit?.document_id;
    const path = fid ? `/files/${fid}` : `/orders/${id}/affidavit/download`;
    try {
      const resp = await api.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.data.type || "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `affidavit_${order.job_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to download affidavit");
    }
  };

  const viewAffidavit = async () => {
    const fid = order.affidavit?.document_id;
    if (!fid) { await downloadAffidavit(); return; }
    const tab = window.open("about:blank", "_blank");
    try {
      const { data } = await api.post(`/files/${fid}/view-token`);
      const url = `${API}/files/${fid}?token=${encodeURIComponent(data.token)}&inline=1`;
      if (tab) tab.location.replace(url); else window.location.assign(url);
    } catch (e) {
      if (tab) tab.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to open affidavit");
    }
  };

  // Combined ARPS cover (page 1) + existing service documents — same authenticated blob mechanism.
  const downloadPackage = async () => {
    try {
      const resp = await api.get(`/orders/${id}/service-package`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `service_package_${order.job_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to download service package");
    }
  };

  const downloadCover = async () => {
    try {
      const resp = await api.get(`/orders/${id}/cover-letter`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cover_${order.job_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to download cover sheet");
    }
  };

  const hasAffidavit = !!order.affidavit;
  const doRefund = async (action, note = "") => {
    setRefundBusy(action);
    try {
      await api.post(`/orders/${id}/refund/${action}`, { note });
      toast.success(action === "approve" ? "Refund approved" : action === "reject" ? "Refund rejected" : "Marked refunded");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefundBusy(""); }
  };

  const canReview = isAdmin && order.status === "pending_review";
  const canReassign = isAdmin && !!order.assigned_server_id && ["assigned", "in_progress", "served", "not_served"].includes(order.status);
  const eligibleServers = repl?.candidates || [];
  const selectedCandidate = eligibleServers.find((c) => c.id === reassignTo);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <button data-testid="back-button" onClick={() => nav(backTo)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Job #{order.job_number}</h2>
            <StatusBadge status={order.status} pulse={["in_progress", "assigned"].includes(order.status)} />
            {order.status !== "completed" && <DeadlineBadge status={order.deadline_status} days={order.days_to_deadline} />}
            {isAdmin && order.client_upright_confirmed === false && (
              <span data-testid="client-upright-flag" className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">Client not confirmed upright</span>
            )}
            {(order.linked_orders || []).map((l) => (
              <span key={l.order_id} data-testid="linked-order-badge" onClick={() => nav(`${isAdmin ? "/admin/jobs" : "/client/orders"}/${l.order_id}`)}
                className="cursor-pointer rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                Linked to {l.job_number}
              </span>
            ))}
          </div>
          <p className="mt-1 text-slate-500">
            {order.service_type} service · {order.defendant}
            {order.assigned_server_name && <> · Server: <span className="font-medium text-slate-700">{order.assigned_server_name}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button data-testid="download-service-package-button" variant="outline" onClick={downloadPackage}>
              <FileText className="mr-1.5 h-4 w-4" /> Download service package
            </Button>
          )}
          {isAdmin && !!order.assigned_server_id && (
            <Button data-testid="download-cover-letter-button" variant="outline" onClick={downloadCover}>
              <FileText className="mr-1.5 h-4 w-4" /> Cover sheet only
            </Button>
          )}
          {(order.status === "completed" && hasAffidavit) && (
            <Button data-testid="download-affidavit-button" onClick={downloadAffidavit} className="bg-emerald-600 text-white hover:bg-emerald-700">
              <Download className="mr-1.5 h-4 w-4" /> Download affidavit
            </Button>
          )}
          {isAdmin && hasAffidavit && (
            <Button data-testid="view-affidavit-button" variant="outline" onClick={viewAffidavit}>
              <FileText className="mr-1.5 h-4 w-4" /> View affidavit
            </Button>
          )}
        </div>
      </div>

      {!isAdmin && order.addon_options && order.addon_options.show && (
        <AddAddressOptions order={order} onDone={load} />
      )}

      {/* Admin action bar */}
      {isAdmin && order.status === "pending_assignment" && (
        <div data-testid="assign-panel" className="flex flex-wrap items-end gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex-1 min-w-52">
            <p className="mb-1.5 text-sm font-semibold text-amber-800">Assign a process server</p>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger data-testid="assign-server-select" className="bg-white"><SelectValue placeholder="Select server" /></SelectTrigger>
              <SelectContent>
                {servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.active_jobs} active</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="confirm-assign-button" onClick={assign} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">
            <UserPlus className="mr-1.5 h-4 w-4" /> Assign
          </Button>
        </div>
      )}

      {isAdmin && order.qa && !["not_monitored", "no_deadline"].includes(order.qa.qa_status) && (
        <div data-testid="qa-panel" className={`rounded-xl border p-4 ${order.qa.escalated ? "border-red-300 bg-red-50" : order.qa.reassignment_recommended ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">QA monitoring</span>
              <QaBadge status={order.qa.qa_status} testid="qa-panel-badge" />
            </div>
            <div className="text-xs text-slate-500">
              {order.qa.hours_remaining != null && <span data-testid="qa-hours-remaining">{order.qa.hours_remaining}h left</span>}
              {order.qa.pct_remaining != null && <span> · {order.qa.pct_remaining}% of window</span>}
              <span> · {order.qa.attempts} attempt(s) · activity: {order.qa.activity_level}</span>
            </div>
          </div>
          <p className="mt-2 text-sm text-slate-600" data-testid="qa-reason">{order.qa.reason}</p>
          {order.qa.recommendation && <p className="mt-1 text-sm font-medium text-red-700" data-testid="qa-recommendation">→ {order.qa.recommendation}</p>}
          {order.qa.escalated && <p className="mt-1 text-xs font-semibold text-red-700" data-testid="qa-escalated-flag">In escalation queue — use Reassign to intervene.</p>}
        </div>
      )}

      {isAdmin && order.pending_refund && (
        <div data-testid="refund-panel" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-900">Refund — access-barrier close{order.pending_refund.close_reason_label ? ` (${order.pending_refund.close_reason_label})` : ""}</p>
          <div className="mt-2 max-w-xs text-sm text-slate-700">
            <div className="flex justify-between"><span>Documented service attempt(s)</span><b data-testid="refund-provider">{fmtMoney(order.currency, order.pending_refund.provider_total)}</b></div>
            <div className="flex justify-between"><span>Administrative fee</span><b>{fmtMoney(order.currency, order.pending_refund.administrative_fee)}</b></div>
            <div className="mt-1 flex justify-between border-t border-emerald-200 pt-1 text-emerald-700"><span>Refund</span><b data-testid="refund-amount">{fmtMoney(order.currency, order.pending_refund.refund)}</b></div>
          </div>
          {order.pending_refund.status === "pending_admin_approval" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button data-testid="refund-approve" disabled={!!refundBusy} onClick={() => doRefund("approve")} className="bg-emerald-600 text-white hover:bg-emerald-700">{refundBusy === "approve" ? "Processing…" : "Approve & issue refund"}</Button>
              <Button data-testid="refund-reject" variant="outline" disabled={!!refundBusy} onClick={() => doRefund("reject")} className="border-red-200 text-red-600 hover:bg-red-50">Reject</Button>
            </div>
          )}
          {order.pending_refund.status === "awaiting_manual_refund" && (
            <div className="mt-3">
              <p className="text-xs text-amber-700">PayPal-paid order — issue the refund in PayPal, then record it here.</p>
              <Button data-testid="refund-mark-manual" disabled={!!refundBusy} onClick={() => doRefund("mark-manual-refunded")} className="mt-2 bg-amber-600 text-white hover:bg-amber-700">Mark refunded (PayPal)</Button>
            </div>
          )}
          {order.pending_refund.status === "refunded" && (
            <p className="mt-2 text-sm font-medium text-emerald-700" data-testid="refund-done">Refunded{order.pending_refund.processor ? ` via ${order.pending_refund.processor.replace("_", " ")}` : ""}.</p>
          )}
          {order.pending_refund.status === "rejected" && (
            <p className="mt-2 text-sm font-medium text-red-600" data-testid="refund-rejected">Refund rejected.</p>
          )}
        </div>
      )}

      {canReassign && (
        <div data-testid="reassign-panel" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <div>
            <p className="text-sm font-semibold text-indigo-900">Currently assigned: <span data-testid="reassign-current-server">{order.assigned_server_name}</span></p>
            <p className="mt-0.5 text-xs text-indigo-700">
              <span data-testid="reassign-eligible-count">{repl ? repl.eligible_count : "…"}</span> qualified replacement server(s) available ·{" "}
              {repl?.hours_to_deadline != null
                ? <span data-testid="reassign-time-remaining">{repl.hours_to_deadline}h to deadline</span>
                : <span data-testid="reassign-time-remaining">No deadline set</span>}
              {repl?.qa && !["not_monitored", "no_deadline"].includes(repl.qa.qa_status) && <> · QA: <span data-testid="reassign-qa-status" className="font-medium">{repl.qa.qa_status.replace(/_/g, " ")}</span></>}
            </p>
            <p className="mt-0.5 text-xs text-indigo-600">Only servers that pass the authoritative approval gate are shown. Deadline, attempts, documents, billing and audit history are preserved.</p>
          </div>
          <Dialog open={reassignOpen} onOpenChange={(v) => { setReassignOpen(v); if (v) loadReplacements(); }}>
            <DialogTrigger asChild>
              <Button data-testid="reassign-server-button" className="bg-indigo-600 text-white hover:bg-indigo-700"><UserPlus className="mr-1.5 h-4 w-4" /> Reassign server</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reassign Job #{order.job_number}</DialogTitle>
                <DialogDescription>Search and select a qualified replacement server, then provide a reason.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-sm">
                  <div><span className="text-slate-500">Current server:</span> <span className="font-semibold text-slate-900">{order.assigned_server_name}</span></div>
                  <div><span className="text-slate-500">Deadline:</span> <span className="font-medium text-slate-900">{order.deadline || "—"}</span></div>
                  <div><span className="text-slate-500">Time remaining:</span> <span className="font-medium text-slate-900">{repl?.hours_to_deadline != null ? `${repl.hours_to_deadline}h` : "—"}</span></div>
                  <div><span className="text-slate-500">QA status:</span> <span className="font-medium text-slate-900">{repl?.qa?.qa_status?.replace(/_/g, " ") || "—"}</span></div>
                </div>
                <div>
                  <p className="mb-1 text-sm font-medium text-slate-700">Qualified replacement server <span className="text-red-500">*</span></p>
                  <Popover open={reassignPickerOpen} onOpenChange={setReassignPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" role="combobox" data-testid="reassign-server-select" className="w-full justify-between font-normal">
                        <span className={selectedCandidate ? "truncate text-slate-900" : "truncate text-slate-400"}>
                          {selectedCandidate ? `${selectedCandidate.name} — ${selectedCandidate.email}` : "Search a qualified server by name or email"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput data-testid="reassign-server-search" placeholder="Type a name or email…" />
                        <CommandList>
                          <CommandEmpty>No matching qualified server.</CommandEmpty>
                          {eligibleServers.map((s) => (
                            <CommandItem
                              key={s.id}
                              value={`${s.name} ${s.email}`}
                              data-testid={`reassign-option-${s.id}`}
                              onSelect={() => { setReassignTo(s.id); setReassignPickerOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${reassignTo === s.id ? "opacity-100" : "opacity-0"}`} />
                              <span className="flex-1 truncate">{s.name} <span className="text-slate-400">— {s.email}</span></span>
                              <span className="ml-2 text-xs text-slate-400">{s.active_jobs} active</span>
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {eligibleServers.length === 0 && <p className="mt-1 text-sm text-slate-400">No other qualified servers</p>}
                </div>
                {selectedCandidate && (
                  <div data-testid="reassign-qualification" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                    <p className="mb-1.5 font-semibold">Qualification (authoritative checks)</p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Account complete</li>
                      <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> All required documents approved ({selectedCandidate.qualification.docs_approved}/{selectedCandidate.qualification.docs_required})</li>
                      <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Admin account approval (active)</li>
                      <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Not the current server</li>
                      <li className="flex items-center gap-1.5 text-slate-500"><CheckCircle2 className="h-3.5 w-3.5 text-slate-400" /> Jurisdiction/service-type: {selectedCandidate.qualification.jurisdiction_enforced ? "matched" : "not tracked yet (not enforced)"}</li>
                    </ul>
                  </div>
                )}
                <div>
                  <p className="mb-1 text-sm font-medium text-slate-700">Reason <span className="text-red-500">*</span></p>
                  <Textarea data-testid="reassign-reason" value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} placeholder="e.g. No activity before deadline — reassignment required." rows={2} />
                </div>
                <div>
                  <p className="mb-1 text-sm font-medium text-slate-700">Admin note (optional)</p>
                  <Textarea data-testid="reassign-note" value={reassignNote} onChange={(e) => setReassignNote(e.target.value)} rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
                <Button data-testid="confirm-reassign-button" onClick={reassign} disabled={busy || !reassignTo} className="bg-indigo-600 text-white hover:bg-indigo-700">Confirm reassignment</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {canReview && (
        <div data-testid="review-panel" className="rounded-xl border border-sky-200 bg-sky-50 p-4">
          <p className="text-sm font-semibold text-sky-800">Review submitted proof</p>
          <p className="text-xs text-sky-600">Verify GPS, photos and affidavit below before approving.</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Textarea data-testid="review-notes" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Optional notes (required for rejection)" className="flex-1 min-w-52 bg-white" rows={2} />
            {order.print_request?.requested && (
              <div className="min-w-52">
                <p className="mb-1 text-xs font-medium text-sky-700">Print ({order.print_request.pages} pg) billed to</p>
                <Select value={printBillable} onValueChange={setPrintBillable}>
                  <SelectTrigger data-testid="print-billable-select" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="server">Server (deduct from payout)</SelectItem>
                    <SelectItem value="client">Client (add to invoice)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Button data-testid="affidavit-approve-button" onClick={() => review("approve")} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve & seal
              </Button>
              <Button data-testid="affidavit-reject-button" onClick={() => review("reject")} disabled={busy} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                <XCircle className="mr-1.5 h-4 w-4" /> Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 font-display text-base font-bold text-slate-900">Case & service</h3>
            <InfoRow label="Defendant" value={order.defendant} />
            <InfoRow label="Plaintiff" value={order.plaintiff} />
            <InfoRow label="Case #" value={order.case_number} />
            <InfoRow label="Jurisdiction" value={order.jurisdiction} />
            <InfoRow label="Service type" value={order.service_type} />
            <InfoRow label="Deadline" value={order.deadline} />
          </div>
          {(order.service_windows || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="service-windows-panel">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-slate-900">Authorized service windows</h3>
                {isAdmin && (
                  <Dialog open={windowsOpen} onOpenChange={setWindowsOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="edit-windows-button" size="sm" variant="outline" onClick={openWindows}>Modify</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Modify service windows</DialogTitle>
                        <DialogDescription>Adjust the authorized scheduling windows for this order. The previous windows are preserved in the audit trail; actual attempt records are unaffected.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2">
                        {editWindows.map((w, i) => (
                          <div key={i} className="flex items-center gap-2" data-testid={`edit-window-row-${i}`}>
                            <Input className="w-32" value={w.label} onChange={(e) => setEditWindows(editWindows.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label" data-testid={`edit-window-label-${i}`} />
                            <Input className="flex-1" value={w.display} onChange={(e) => setEditWindows(editWindows.map((x, j) => j === i ? { ...x, display: e.target.value } : x))} placeholder="8:00 AM–12:00 PM" data-testid={`edit-window-display-${i}`} />
                            <button data-testid={`remove-window-${i}`} onClick={() => setEditWindows(editWindows.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><XCircle className="h-4 w-4" /></button>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" data-testid="add-window-button" onClick={() => setEditWindows([...editWindows, { key: "", label: "", display: "", start: "", end: "", status: "scheduled" }])}>Add window</Button>
                        <Textarea data-testid="windows-reason" value={windowsReason} onChange={(e) => setWindowsReason(e.target.value)} placeholder="Reason for modification (optional — recorded in audit trail)" rows={2} />
                      </div>
                      <DialogFooter>
                        <Button data-testid="save-windows-button" onClick={saveWindows} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">Save windows</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <p className="mb-2 text-xs text-slate-500">Standard Service: up to {order.service_standard?.attempts || 4} diligent attempts. Scheduling windows — actual attempt times are recorded separately.</p>
              {order.service_windows.map((w) => (
                <InfoRow key={w.sequence || w.key} label={`${w.sequence ? w.sequence + ". " : ""}${w.label}`} value={w.display} />
              ))}
            </div>
          )}
          {(order.invoice || order.compensation) && (
            <div data-testid="billing-panel" className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 font-display text-base font-bold text-slate-900">Billing</h3>
              {order.invoice && (
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-mono-arps text-sm font-semibold text-slate-900">{order.invoice.invoice_number}</span>
                    <PayBadge status={order.invoice.payment_status} />
                  </div>
                  <InfoRow label="Total" value={fmtMoney(order.invoice.currency, order.invoice.total)} />
                  <InfoRow label="Balance due" value={fmtMoney(order.invoice.currency, order.invoice.balance_due)} />
                  <a data-testid="order-view-receipt" href={`${API}/invoices/${order.invoice.id}/receipt`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:underline">
                    <Download className="h-3.5 w-3.5" /> View receipt
                  </a>
                  {isAdmin && order.invoice.payment_status !== "paid" && order.invoice.stripe_session_id && (
                    <Button
                      data-testid="order-sync-stripe"
                      size="sm"
                      variant="outline"
                      disabled={syncingStripe}
                      onClick={async () => {
                        setSyncingStripe(true);
                        try {
                          const { data } = await api.post("/admin/stripe/reconcile", { invoice_id: order.invoice.id });
                          const r = (data.results || [])[0] || {};
                          if (r.action === "reconciled_paid") { toast.success(`${order.invoice.invoice_number} reconciled — marked Paid.`); load(); }
                          else if (r.action === "already_paid" || r.action === "already_recorded") toast.message("Already reconciled — no changes made.");
                          else if (r.action === "not_paid_in_stripe" || r.action === "not_paid") toast.message("No successful Stripe payment found for this invoice.");
                          else toast.message(r.detail ? `Sync: ${r.detail}` : "Sync complete.");
                        } catch (e) {
                          toast.error(formatApiErrorDetail(e) || "Could not sync with Stripe.");
                        } finally { setSyncingStripe(false); }
                      }}
                      className="ml-2 mt-2 inline-flex items-center gap-1.5"
                    >
                      <Download className={`h-3.5 w-3.5 ${syncingStripe ? "animate-spin" : ""}`} /> {syncingStripe ? "Syncing…" : "Sync with Stripe"}
                    </Button>
                  )}
                  <div className="mt-3"><PrintingCharge orderId={id} role={isAdmin ? "admin" : "client"} onStatus={setPrintBlocked} onChanged={load} /></div>
                  {user.role === "client" && (
                    printBlocked ? (
                      <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-center text-xs text-amber-700" data-testid="order-pay-blocked-note">Confirm the document printing charge above to enable payment.</p>
                    ) : (
                      <PayInvoiceButton invoice={{ ...order.invoice, job_number: order.job_number }} onPaid={() => window.location.reload()} />
                    )
                  )}
                </div>
              )}
              {order.compensation && (
                <div className="border-t border-slate-100 pt-3">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Server compensation</p>
                  <InfoRow label="Net payout" value={fmtMoney(order.compensation.currency, order.compensation.net_payout)} />
                  <InfoRow label="Status" value={(order.compensation.payment_status || "").toUpperCase()} />
                </div>
              )}
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-2 flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><MapPin className="h-4 w-4 text-emerald-600" /> Service address</h3>
            <p className="text-sm text-slate-700">{addr.street}</p>
            <p className="text-sm text-slate-700">{addr.city}, {addr.state} {addr.zip}</p>
          </div>
          {(order.documents || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 font-display text-base font-bold text-slate-900">Documents</h3>
              {order.documents.map((d) => (
                <a key={d} data-testid={`doc-link-${d}`} href={`${API}/files/${d}`} target="_blank" rel="noreferrer" className="mb-1.5 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50">
                  <FileText className="h-4 w-4 text-slate-400" /> View document
                </a>
              ))}
            </div>
          )}
          {order.instructions && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-2 font-display text-base font-bold text-slate-900">Instructions</h3>
              <p className="text-sm text-slate-600">{order.instructions}</p>
            </div>
          )}
          {order.client_decision?.state === "awaiting" && <ClientDecisionPanel order={order} role={user?.role} />}
          {order.skip_trace && <SkipTracePanel order={order} role={user?.role} onChanged={load} />}
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 font-display text-base font-bold text-slate-900">Service attempts & evidence</h3>
            <EvidenceChain orderId={id} role={user?.role} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 font-display text-base font-bold text-slate-900">Audit trail</h3>
            <AuditTrail order={order} />          </div>
          <DocumentRequests orderId={id} role={user?.role || "client"} orderStatus={order.status} />
          <DocumentVault orderId={id} role={user?.role || "client"} />
        </div>
        {user.role === "client" && <NeedHelp jobNumber={order.job_number} className="lg:col-span-3" />}
      </div>
    </div>
  );
}
